/**
 * The adapter types an operator can pick for an aircraft (mockup
 * `fsl-rtm-map-mu` → Inventory → Custodian Configuration → Adapter Type).
 *
 * `value` is exactly what is written to the Custodian API's `adapter_type`
 * (`PUT /drones/{serial}/adapter-config`). The API itself accepts any text —
 * there is no server-side list (ENG-227) — so this list is the frontend's own.
 *
 * Stored values are NOT consistently spelled: the sandbox database holds
 * `mavlink`, the airmarket tenant holds an uppercase `DJI`, and older records
 * use `dji_fh2` / `adsb_out`. Everything that compares an adapter type must go
 * through `normaliseAdapter` / `isMavlinkAdapter` instead of `===`.
 */
export const ADAPTER_OPTIONS = [
  { value: 'DJI', label: 'DJI (FH2 / Cloud API)' },
  { value: 'MAVLink', label: 'MAVLink' },
  { value: 'ADS-B Out', label: 'ADS-B Out' },
] as const;

export type AdapterOptionValue = (typeof ADAPTER_OPTIONS)[number]['value'];

/** Lowercased, punctuation stripped: `ADS-B Out`, `adsb_out` and `ADSB Out`
 *  all collapse to `adsbout`. */
const squash = (raw: string) => raw.toLowerCase().replace(/[^a-z0-9]/g, '');

const ALIASES: Record<string, AdapterOptionValue> = {
  mavlink: 'MAVLink',
  dji: 'DJI',
  djifh2: 'DJI',
  adsbout: 'ADS-B Out',
};

/** Which dropdown option a stored `adapter_type` corresponds to, or null for an
 *  empty value or one we don't list (a backend-added type). */
export function normaliseAdapter(raw: string | null | undefined): AdapterOptionValue | null {
  const key = squash(raw ?? '');
  return key && Object.hasOwn(ALIASES, key) ? ALIASES[key]! : null;
}

/** MAVLink aircraft are the ones with a host / port / protocol connection. */
export function isMavlinkAdapter(raw: string | null | undefined): boolean {
  return normaliseAdapter(raw) === 'MAVLink';
}

/** The value the dropdown should show for a stored `adapter_type`: the matching
 *  option, else the raw stored value (rendered as an extra option so it doesn't
 *  read as "nothing selected"), else '' for no value at all. */
export function adapterSelectValue(raw: string | null | undefined): string {
  return normaliseAdapter(raw) ?? (raw ?? '').trim();
}

/** Whether saving `selected` would change the stored type. `mavlink` stored and
 *  `MAVLink` selected is the same adapter, so it must NOT trigger a write that
 *  only re-spells the value. */
export function adapterChanged(stored: string | null | undefined, selected: string): boolean {
  if (!selected) return false;
  return adapterSelectValue(stored) !== selected;
}
